import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../../util/reactIntl';

import { ListingCard } from '../../../../components';

import IconChevronLeft from './icons/IconChevronLeft';
import IconChevronRight from './icons/IconChevronRight';

import css from './SectionListingStrip.module.css';

// Pixel slop when comparing scroll position to its bounds. Browsers can
// report `scrollLeft` as a fractional value; treating anything within a
// pixel as "at the edge" prevents disabled-state flicker.
const SCROLL_EDGE_EPSILON = 1;

/**
 * Hook: tracks whether the scroller can be scrolled left/right and whether
 * its content overflows. Recomputes on scroll, on resize, and whenever the
 * item count changes (a primitive dep that's safe to use in `useEffect`).
 */
const useScrollEdges = (scrollerRef, itemCount) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    const recompute = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const maxScroll = scrollWidth - clientWidth;
      setOverflows(maxScroll > SCROLL_EDGE_EPSILON);
      setCanScrollLeft(scrollLeft > SCROLL_EDGE_EPSILON);
      setCanScrollRight(scrollLeft < maxScroll - SCROLL_EDGE_EPSILON);
    };

    recompute();
    el.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);
    return () => {
      el.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCount]);

  return { canScrollLeft, canScrollRight, overflows };
};

/**
 * The horizontal row of listing cards with its previous and next arrows, as
 * on the homepage strips. Shared by SectionListingStrip and the Butikker
 * page's hand-picked carousel; the caller owns the heading and decides what
 * to show when there are no listings.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.listings - listing entities, in display order
 * @returns {JSX.Element}
 */
const ListingCarousel = props => {
  const { listings } = props;
  const intl = useIntl();
  const scrollerRef = useRef(null);
  const { canScrollLeft, canScrollRight, overflows } = useScrollEdges(scrollerRef, listings.length);

  // Advance the carousel by a full page of cards. We scroll by a whole
  // multiple of the card "pitch" (card width + gap) rather than by
  // clientWidth — clientWidth includes the scroller's edge padding, so
  // scrolling by it drifts off the card grid and a page lands mid-card
  // (one card flush at the edge, a sliver of the next visible). Measuring
  // the pitch from the DOM keeps the gap/margin accounted for, so each
  // page lands cleanly on a card boundary (e.g. exactly 2 cards on mobile).
  const scrollBy = direction => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    const cards = el.children;
    const step = cards.length >= 2 ? cards[1].offsetLeft - cards[0].offsetLeft : 0;
    if (step <= 0) {
      // Fallback: can't measure a pitch (one card), nudge by viewport width.
      el.scrollBy({ left: el.clientWidth * direction, behavior: 'smooth' });
      return;
    }
    // Whole cards visible across the scroller = one page.
    const perPage = Math.max(1, Math.round(el.clientWidth / step));
    // scrollLeft is a multiple of `step` at aligned positions (the edge
    // padding cancels against scroll-padding-left), so rounding snaps any
    // drift back to the grid before advancing a page.
    const currentIndex = Math.round(el.scrollLeft / step);
    const targetIndex = Math.max(0, currentIndex + perPage * direction);
    el.scrollTo({ left: targetIndex * step, behavior: 'smooth' });
  };

  const showArrows = listings.length > 0 && overflows;

  return (
    <div className={css.carousel}>
      <div className={css.scroller} ref={scrollerRef}>
        {listings.map(listing => (
          <div key={listing.id.uuid} className={css.cardWrapper}>
            <ListingCard listing={listing} />
          </div>
        ))}
      </div>

      {showArrows ? (
        <>
          <button
            type="button"
            className={classNames(css.arrow, css.arrowPrev)}
            onClick={() => scrollBy(-1)}
            disabled={!canScrollLeft}
            aria-label={intl.formatMessage({ id: 'SectionListingStrip.scrollPrev' })}
          >
            <IconChevronLeft className={css.arrowIcon} />
          </button>
          <button
            type="button"
            className={classNames(css.arrow, css.arrowNext)}
            onClick={() => scrollBy(1)}
            disabled={!canScrollRight}
            aria-label={intl.formatMessage({ id: 'SectionListingStrip.scrollNext' })}
          >
            <IconChevronRight className={css.arrowIcon} />
          </button>
        </>
      ) : null}
    </div>
  );
};

export default ListingCarousel;
